import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
  getInvestigationReport,
  investigatedInstant,
  type IInvestigationReport,
} from '@apis/investigationReports';
import { FloorSvg } from '@components/FloorSvg/FloorSvg';
import { CardIcon } from '@components/Pages/AnalyticsPage/CardIcon';
import { PageLoader } from '@components/Pages/AnalyticsPage/Reports/shared/PageLoader';
import { ReportNotFoundState } from '@components/Pages/AnalyticsPage/Reports/shared/ReportNotFoundState';
import { ReportErrorState } from '@components/Pages/AnalyticsPage/Reports/shared/ReportErrorState';
import { ReportEmptyState } from '@components/Pages/AnalyticsPage/Reports/shared/ReportEmptyState';
import { ReportHeader } from '@components/Pages/AnalyticsPage/Reports/shared/ReportHeader/ReportHeader';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ, formatLongDate, formatTime, formatYmd } from '@utils/dateUtils';
import type { SafetyEvent } from '@apis/reports';
import { useIncidentReportQuery } from './hooks/useIncidentReportQuery';
import { useIncidentTagSafetyEventsQuery } from './hooks/useIncidentTagSafetyEventsQuery';
import { useRangeSafetyEventsQuery } from './hooks/useRangeSafetyEventsQuery';
import { useStopActivitiesQuery } from '../shared/hooks/useStopActivitiesQuery';
import { IncidentSummaryCard } from './components/IncidentSummaryCard';
import { StopsRail } from '../shared/StopsRail';
import { StopDetail } from './components/StopDetail';
import styles from './styles.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

/** Display half-window in minutes — mirrors the BFF's `INCIDENT_WINDOW_MS`. */
const INCIDENT_WINDOW_MIN = 30;

/**
 * Route entry-point for the Incident Report analytics page. Composes the
 * archived submission with the live route compute + safety-event queries,
 * and renders the shared chassis (`<ReportHeader>`, `<ReportStatePanel>`
 * wrappers) plus the Incident-specific summary card and stop-detail panel.
 */
export function IncidentReportReportPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ja' ? 'ja' : 'en';
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;
  const { siteId = '', reportId = '' } = useParams<{
    siteId: string;
    reportId: string;
  }>();

  const recordQuery = useQuery({
    queryKey: ['investigationReport', siteId, reportId] as const,
    queryFn: ({ signal }) => getInvestigationReport(siteId, reportId, signal),
    enabled: !!siteId && !!reportId,
    staleTime: 5 * 60 * 1000,
  });
  const record = recordQuery.data ?? null;
  const recordIsIncident = record?.investigationType === 'incidentReport';

  // No analysed instant → nothing to reconstruct the incident around. Empty
  // params keep the query dormant (an `atTimestamp: 0` fetch would ask
  // upstream about 1970) and the explicit state renders below.
  const analysedInstant = recordIsIncident ? investigatedInstant(record) : undefined;
  const params =
    recordIsIncident && analysedInstant
      ? deriveIncidentParams(record, analysedInstant)
      : { entityKind: 'tag' as const, entityId: '', atTimestamp: 0 };

  const incident = useIncidentReportQuery({ siteId, ...params });
  const incidentData = incident.data;

  const [userSelectedIndex, setUserSelectedIndex] = useState<number | null>(null);

  const stops = useMemo(() => incidentData?.stops ?? [], [incidentData?.stops]);
  const incidentIndex = incidentData?.atStopIndex ?? null;
  const effectiveSelectedIndex = resolveEffectiveSelectedIndex(
    stops,
    userSelectedIndex,
    incidentIndex,
  );
  const selected =
    stops.find((stop) => stop.stopIndex === effectiveSelectedIndex) ?? null;
  const selectedFloorId = selected?.floorId ?? '';
  const selectedAreaId = selected?.areaId ?? '';
  const selectedArrivalMs = selected ? Date.parse(selected.arrivalIso) : 0;
  const selectedDepartureMs = selected ? Date.parse(selected.departureIso) : 0;
  const incidentDateYmd = incidentData
    ? formatYmd(new Date(incidentData.atMs), tz)
    : '';

  const subjectSafety = useIncidentTagSafetyEventsQuery({
    siteId,
    tagId: params.entityKind === 'tag' ? params.entityId : '',
    atTimestamp: incidentData?.atMs ?? 0,
    enabled: !!incidentData && params.entityKind === 'tag',
  });
  const stopSafety = useRangeSafetyEventsQuery({
    siteId,
    startTimestamp: selectedArrivalMs,
    endTimestamp: selectedDepartureMs,
    areaId: selectedAreaId || undefined,
    floorId: selectedFloorId || undefined,
    enabled: !!selected,
  });
  const activities = useStopActivitiesQuery({
    siteId,
    floorId: selectedFloorId,
    date: incidentDateYmd,
  });

  const { resolveAreaNick, resolveFloorNick } = useMemo(() => {
    const areaMap = new Map<string, string>();
    const floorMap = new Map<string, string>();
    for (const stop of stops) {
      areaMap.set(stop.areaId, stop.areaNick);
      floorMap.set(stop.floorId, stop.floorNick);
    }
    return {
      resolveAreaNick: (areaId: string | null) =>
        areaId === null ? null : areaMap.get(areaId) ?? areaId,
      resolveFloorNick: (floorId: string | null) =>
        floorId === null ? null : floorMap.get(floorId) ?? floorId,
    };
  }, [stops]);

  const resolveStopIndexForEvent = useMemo(() => {
    return (event: Pick<SafetyEvent, 'timestamp' | 'floorId' | 'areaId'>) => {
      const eventMs = Date.parse(event.timestamp);
      const byTime = stops.find((stop) => {
        const arrivalMs = Date.parse(stop.arrivalIso);
        const departureMs = Date.parse(stop.departureIso);
        return eventMs > arrivalMs && eventMs < departureMs;
      });
      if (byTime) return byTime.stopIndex;
      if (event.floorId === null) return null;
      const byLocPrecise = stops.find(
        (stop) =>
          stop.floorId === event.floorId &&
          (event.areaId === null || stop.areaId === event.areaId),
      );
      if (byLocPrecise) return byLocPrecise.stopIndex;
      const byFloor = stops.find((stop) => stop.floorId === event.floorId);
      return byFloor?.stopIndex ?? null;
    };
  }, [stops]);

  // -- Render branches ------------------------------------------------------

  if (recordQuery.isLoading) {
    return <PageLoader ariaLabelKey="reports.incidentReport.title" />;
  }
  if (!record || !recordIsIncident) {
    return (
      <ReportNotFoundState
        backHref={`/analytics/${siteId}`}
        notFoundKey="reports.incidentReport.notFound"
        backKey="reports.incidentReport.notFoundBack"
      />
    );
  }
  if (!analysedInstant) {
    return (
      <ReportNotFoundState
        backHref={`/analytics/${siteId}`}
        notFoundKey="reports.shared.noAnalysedDate"
        backKey="reports.incidentReport.notFoundBack"
      />
    );
  }
  if (incident.isLoading) {
    return <PageLoader ariaLabelKey="reports.incidentReport.title" />;
  }
  if (incident.error) {
    return (
      <ReportErrorState
        titleKey="reports.incidentReport.errorRetry"
        retryLabelKey="reports.incidentReport.retry"
        onRetry={() => incident.refetch()}
      />
    );
  }

  // Both render branches (no-stops + happy path) share the same header.
  // Hoist so changes to either don't need to be made twice.
  const atMsForHeader = incidentData?.atMs ?? params.atTimestamp;
  const dateText = atMsForHeader
    ? `${formatLongDate(new Date(atMsForHeader), lang, tz)}, ${formatTime(new Date(atMsForHeader), lang, tz)}`
    : '';
  const windowBadge = (
    <span className={styles.windowBadge}>
      <CardIcon name="clock" size={14} />
      {t('reports.incidentReport.plusMinusMins', { m: INCIDENT_WINDOW_MIN })}
    </span>
  );
  const reportHeader = (
    <ReportHeader
      siteId={siteId}
      reportId={reportId}
      record={record}
      defaults={{
        title: t('reports.incidentReport.title'),
        description: t('reports.incidentReport.subtitle'),
      }}
      backHref={`/analytics/${siteId}`}
      backLabel={t('reports.incidentReport.notFoundBack')}
      renameLabel={t('reports.incidentReport.rename')}
      dateText={dateText}
      rightSlot={windowBadge}
    />
  );

  if (!incidentData || incidentData.stops.length === 0) {
    return (
      <div className={styles.page}>
        {reportHeader}
        <ReportEmptyState titleKey="reports.incidentReport.noStops" />
      </div>
    );
  }

  const stop = selected ?? incidentData.stops[0]!;

  return (
    <div className={styles.page}>
      {reportHeader}
      <div className={styles.content}>
        <IncidentSummaryCard
          subject={incidentData.subject}
          safetyEvents={subjectSafety.data ?? []}
          resolveAreaNick={resolveAreaNick}
          resolveFloorNick={resolveFloorNick}
          resolveStopIndex={resolveStopIndexForEvent}
          onSelectEvent={(event) => {
            const stopIndex = resolveStopIndexForEvent(event);
            if (stopIndex !== null) setUserSelectedIndex(stopIndex);
          }}
        />
        <div className={styles.body}>
          <StopsRail
            stops={incidentData.stops}
            selectedIndex={effectiveSelectedIndex}
            markedIndex={incidentIndex}
            onSelect={setUserSelectedIndex}
            subjectName={incidentData.subject.name}
            i18nNamespace="reports.incidentReport"
          />
          <ScrollArea className={styles.pinned}>
            <StopDetail
              stop={stop}
              isIncidentStop={stop.stopIndex === incidentIndex}
              activities={{
                data: activities.data,
                isLoading: activities.isLoading,
                isError: activities.isError,
                refetch: activities.refetch,
              }}
              safetyEvents={{
                data: stopSafety.data,
                isLoading: stopSafety.isLoading,
                isError: stopSafety.isError,
                refetch: stopSafety.refetch,
              }}
            />
          </ScrollArea>
          <ScrollArea className={styles.pinned}>
            <div className={styles.floorPane}>
              <FloorSvg
                siteId={siteId}
                floorId={stop.floorId}
                highlightAreaId={stop.areaId}
              />
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Internal helpers                                                            */
/* -------------------------------------------------------------------------- */

/**
 * The incident's moment comes in from the caller: a record without one is a
 * render state, never a substituted instant (querying upstream about the
 * archive date would return a real, believable incident from the wrong time).
 */
function deriveIncidentParams(
  record: IInvestigationReport,
  analysedInstant: Date,
): {
  entityKind: 'tag' | 'equipment';
  entityId: string;
  atTimestamp: number;
} {
  const entityKind = record.entityType ?? 'tag';
  const entityId =
    entityKind === 'tag' ? record.tagId ?? '' : record.equipmentId ?? '';
  return { entityKind, entityId, atTimestamp: analysedInstant.getTime() };
}

function resolveEffectiveSelectedIndex(
  stops: readonly { stopIndex: number }[],
  userSelectedIndex: number | null,
  incidentIndex: number | null,
): number {
  if (stops.length === 0) return 1;
  const first = stops[0]!.stopIndex;
  const last = stops[stops.length - 1]!.stopIndex;
  if (userSelectedIndex == null) return incidentIndex ?? first;
  if (!stops.some((stop) => stop.stopIndex === userSelectedIndex)) return first;
  return Math.min(userSelectedIndex, last);
}
